import { useEffect, useRef, useState, useMemo } from "react";
import axios from "axios";
import RadarChartComponent from "./RadarChartComponent";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost:4000";

function Information(props) {
  const [tabCount, setTabCount] = useState(1);
  const tabMenu = ['土地資訊', '交易資訊', 'AI分析'];
  const [windowInnerWidth, setWindowInnerWidth] = useState(window.innerWidth);
  const [informationScrollValue, setInformationScrollValue] = useState(0);
  const [contentHeightArray, setContentHeightArray] = useState([]);
  const [informationHeight, setInformationHeight] = useState(400);
  const [touchStartY, setTouchStartY] = useState(0);
  const [touchDistanceY, setTouchDistanceY] = useState(0);
  const [selectedBuildingType, setSelectedBuildingType] = useState("");
  const [aiAnalysisResult, setAiAnalysisResult] = useState(null);
  const [isAiDataLoading, setIsAiDataLoading] = useState(false);
  const noneBoxRef = useRef(null);

  const getGradeFromScore = (score) => {
    if (score >= 9) return 'A';
    if (score >= 8) return 'B';
    if (score >= 7) return 'C';
    if (score >= 5.5) return 'D';
    return 'F';
  };

  function mobileTabClick(index) {
    const before_tab_01_height = contentHeightArray[0] + 120;
    const before_tab_02_height = before_tab_01_height + contentHeightArray[1] + 60;
    const before_tab_03_height = before_tab_02_height + contentHeightArray[2] + 60;
    if (noneBoxRef.current) {
      if (index === 1) {
        noneBoxRef.current.scrollTop = before_tab_01_height;
        setTabCount(index);
      } else if (index === 2) {
        noneBoxRef.current.scrollTop = before_tab_02_height;
        setTabCount(index);
      } else {
        noneBoxRef.current.scrollTop = before_tab_03_height;
        setTabCount(index);
      }
    }
  }

  function noneBoxScrollEvent(e) {
    e.preventDefault();
    setInformationScrollValue(e.target.scrollTop);
  }

  function handleTouchStart(e) {
    setTouchStartY(e.touches[0].clientY);
  }

  function handleTouchMove(e) {
    const currentY = e.touches[0].clientY;
    const distance = currentY - touchStartY;
    setTouchDistanceY(distance);
  }

  function handleTouchEnd(e) {
    let upDown = 'same';
    if (touchDistanceY > 50) {
      upDown = 'down';
    } else if (touchDistanceY < -50) {
      upDown = 'up';
    }

    if (informationHeight === 400) {
      if (touchDistanceY > 300) {
        setInformationHeight(20);
      } else {
        if (upDown === 'down') {
          setInformationHeight(100);
        } else if (upDown === 'up') {
          setInformationHeight(400);
        }
      }
    }
    if (informationHeight === 100) {
      if (upDown === 'down') {
        setInformationHeight(20);
      } else if (upDown === 'up') {
        setInformationHeight(400);
      }
    }
    if (informationHeight === 20) {
      if (touchDistanceY < -300) {
        setInformationHeight(400);
      } else {
        if (upDown === 'down') {
          setInformationHeight(20);
        } else if (upDown === 'up') {
          setInformationHeight(100);
        }
      }
    }

    setTouchDistanceY(0);
  }

  function tab_height_check() {
    const noneBox = noneBoxRef.current;
    if (noneBox !== null) {
      const newArray = [];
      noneBox.querySelectorAll('.content_wrap').forEach((item, index) => {
        newArray.push(item.offsetHeight);
      });
      setContentHeightArray(newArray);
    }
  }

  let resetTime;
  function resizeHandle() {
    setWindowInnerWidth(window.innerWidth);
    if (window.innerWidth <= 1024) {
      setTabCount(0);
    } else {
      setTabCount(1);
    }
    clearTimeout(resetTime);
    resetTime = setTimeout(tab_height_check, 1000);
  }

  const formatPrice = (price) => {
    if (!price) return "N/A";
    // 천 단위로 구분하여 표시
    const formatted = new Intl.NumberFormat("zh-TW", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
    return formatted + " 元";
  };

  // InfoWindow용 억 단위 포맷 (대만: 億 = 100,000,000)
  const formatPriceForInfoWindow = (price) => {
    if (!price || price === 0) return "N/A";
    const YI = 100000000; // 1억
    const yiAmount = price / YI;
    
    if (yiAmount >= 1) {
      // 억 단위로 표시 (소수점 1자리까지)
      return yiAmount.toFixed(1) + " 億";
    } else {
      // 억 미만이면 만 단위로 표시 (만 = 10,000)
      const WAN = 10000;
      const wanAmount = price / WAN;
      if (wanAmount >= 1) {
        return wanAmount.toFixed(1) + " 萬";
      } else {
        // 만 미만이면 원 단위로 표시
        return formatPrice(price);
      }
    }
  };

  // zoningData가 배열인 경우 첫 번째 요소 사용
  const zoning = Array.isArray(props.zoningData) ? props.zoningData[0] : props.zoningData;
  
  // urbanPlanningData가 배열인 경우 첫 번째 요소 사용
  const urbanPlanning = Array.isArray(props.urbanPlanningData) ? props.urbanPlanningData[0] : props.urbanPlanningData;

  // 건축 가능한 유형과 예상 공사비 계산
  const applicableBuildingTypes = useMemo(() => {
    if (!props.zoningData || !props.buildingTypes || props.buildingTypes.length === 0) {
      return [];
    }

    const zoneName = zoning?.luse || zoning?.zoneType || zoning?.name;
    if (!zoneName) {
      return [];
    }

    // 해당 용도지역에 적용 가능한 건축 유형 필터링
    const applicable = props.buildingTypes.filter((type) => {
      return (
        type.applicableZoning &&
        type.applicableZoning.length > 0 &&
        type.applicableZoning.includes(zoneName)
      );
    });

    // 각 건축 유형의 예상 공사비 계산
    const landArea = props.cadastralData?.area;
    if (!landArea || landArea <= 0) {
      return applicable.map((type) => ({
        ...type,
        estimatedCost: null,
        costPerSqm: null,
      }));
    }

    return applicable.map((type) => {
      const costData = props.constructionCosts?.[type.code];
      const costPerSqm = costData?.totalCostPerSqm || 0;
      const estimatedCost = costPerSqm > 0 ? costPerSqm * landArea : null;

      return {
        ...type,
        estimatedCost,
        costPerSqm,
      };
    });
  }, [props.zoningData, props.buildingTypes, props.constructionCosts, props.cadastralData?.area, zoning]);

  // 선택된 건축 유형의 예상 공사비
  const selectedBuildingTypeCost = useMemo(() => {
    if (!selectedBuildingType || applicableBuildingTypes.length === 0) {
      return null;
    }
    const selected = applicableBuildingTypes.find((type) => type.code === selectedBuildingType);
    return selected || null;
  }, [selectedBuildingType, applicableBuildingTypes]);

  useEffect(() => {
    tab_height_check();
  }, []);

  // InfoWindow 관리 (열기, 내용 설정, 업데이트 모두 여기서 처리)
  useEffect(() => {
    if (!props.cadastralData || !props.showInformation || !window.infoWindow || !window.map) {
      return;
    }

    const cadastral = props.cadastralData;
    
    // InfoWindow 내용 생성
    let content = '<div class="infoWindow">';
    
    // 선택된 건축 유형명 표시
    if (selectedBuildingTypeCost && selectedBuildingTypeCost.name) {
      content += `<div class="name">${selectedBuildingTypeCost.name}</div>`;
    } else if (selectedBuildingType && applicableBuildingTypes.length > 0) {
      const selected = applicableBuildingTypes.find((type) => type.code === selectedBuildingType);
      if (selected && selected.name) {
        content += `<div class="name">${selected.name}</div>`;
      } else {
        content += '<div class="name">-</div>';
      }
    } else {
      content += '<div class="name">-</div>';
    }
    
    // 예상 공사비 표시 (공사비가 있으면 우선 표시)
    if (selectedBuildingTypeCost && selectedBuildingTypeCost.estimatedCost !== null && selectedBuildingTypeCost.estimatedCost > 0) {
      content += `<div class="allow">預估工程費</div>`;
      const formattedPrice = formatPriceForInfoWindow(selectedBuildingTypeCost.estimatedCost);
      content += `<div class="estimateTotal">${formattedPrice}</div>`;
    } else {
      // 공사비가 없으면 면적 표시
      content += `<div class="allow">土地資訊</div>`;
      if (cadastral && cadastral.area) {
        content += `<div class="estimateTotal">${cadastral.area.toFixed(2)} m²</div>`;
      } else {
        content += '<div class="estimateTotal">-</div>';
      }
    }
    
    content += "</div>";
    
    // InfoWindow 위치 설정 (현재 선택된 토지의 geometry에서 계산)
    let position = null;
    
    if (window.polygonCenter) {
      // Map.js에서 저장한 폴리곤 중심 좌표 사용
      position = { lat: window.polygonCenter.lat, lng: window.polygonCenter.lng };
    } else if (cadastral?.geometry?.coordinates) {
      // geometry에서 직접 중심 좌표 계산
      try {
        const coordinates = cadastral.geometry.coordinates;
        let allCoords = [];
        
        if (cadastral.geometry.type === "Polygon" && coordinates[0]) {
          allCoords = coordinates[0];
        } else if (cadastral.geometry.type === "MultiPolygon" && coordinates[0]?.[0]) {
          allCoords = coordinates[0][0];
        }
        
        if (allCoords.length > 0) {
          // 모든 좌표의 평균으로 중심 계산
          let sumLat = 0;
          let sumLng = 0;
          allCoords.forEach((coord) => {
            sumLat += parseFloat(coord[1]); // GeoJSON은 [lng, lat] 순서
            sumLng += parseFloat(coord[0]);
          });
          position = {
            lat: sumLat / allCoords.length,
            lng: sumLng / allCoords.length
          };
        }
      } catch (error) {
        console.error("Error calculating center from geometry:", error);
      }
    }
    
    if (position) {
      try {
        // InfoWindow 열기 및 내용 설정
        window.infoWindow.setContent(content);
        window.infoWindow.setPosition(position);
        
        // InfoWindow가 아직 열려있지 않으면 열기
        if (!window.infoWindow.getMap()) {
          window.infoWindow.open(window.map);
        }
      } catch (error) {
        console.error("Error updating InfoWindow:", error);
      }
    }
  }, [props.cadastralData, props.showInformation, selectedBuildingTypeCost, selectedBuildingType, applicableBuildingTypes]);

  useEffect(() => {
    window.addEventListener('resize', resizeHandle);
    return () => {
      window.removeEventListener('resize', resizeHandle);
    };
  }, []);

  useEffect(() => {
    const before_tab_01_height = contentHeightArray[0];
    const before_tab_02_height = before_tab_01_height + contentHeightArray[1];
    const before_tab_03_height = before_tab_02_height + contentHeightArray[2] + 60;
    if (informationScrollValue < before_tab_01_height) {
      setTabCount(0);
    }
    if (informationScrollValue > before_tab_01_height && informationScrollValue < before_tab_02_height) {
      setTabCount(1);
    }
    if (informationScrollValue > before_tab_02_height && informationScrollValue < before_tab_03_height) {
      setTabCount(2);
    }
    if (informationScrollValue > before_tab_03_height) {
      setTabCount(3);
    }
  }, [informationScrollValue]);

  // 건축 가능 유형이 변경되면 첫 번째 유형을 자동 선택
  useEffect(() => {
    if (applicableBuildingTypes.length > 0 && !selectedBuildingType) {
      setSelectedBuildingType(applicableBuildingTypes[0].code);
    } else if (applicableBuildingTypes.length === 0) {
      setSelectedBuildingType("");
    }
  }, [applicableBuildingTypes, selectedBuildingType]);

  // cadastralData나 address가 변경되면 AI 분석 결과 초기화
  useEffect(() => {
    setAiAnalysisResult(null);
    setIsAiDataLoading(false);
  }, [props.cadastralData, props.address]);

  // AI 분석 탭이 선택되면 토지평가 데이터 가져오기
  useEffect(() => {
    if (tabCount === 3 && props.cadastralData && props.address && !aiAnalysisResult && !isAiDataLoading) {
      const fetchAiAnalysis = async () => {
        setIsAiDataLoading(true);
        try {
          const cadastral = props.cadastralData;
          const zoning = Array.isArray(props.zoningData) ? props.zoningData[0] : props.zoningData;
          
          // 주소 구성 (대만식 주소 형식)
          const addressStr = [
            props.address.administrative_area_level_1,
            props.address.administrative_area_level_2,
            props.address.route,
            props.address.street_number ? (props.address.street_number.includes('號') ? props.address.street_number : props.address.street_number + '號') : ''
          ].filter(Boolean).join(' ');

          // 주변 정보 구성 (거래 정보, 건물 정보 등)
          const nearbyInfoParts = [];
          if (props.transactionData && Array.isArray(props.transactionData) && props.transactionData.length > 0) {
            nearbyInfoParts.push(`附近有${props.transactionData.length}筆交易記錄`);
          }
          if (props.buildingData) {
            nearbyInfoParts.push('附近有建築物');
          }
          const nearbyInfo = nearbyInfoParts.length > 0 ? nearbyInfoParts.join('，') : undefined;

          const response = await axios.post(`${API_BASE_URL}/land-evaluation/analyze`, {
            address: addressStr || '地址資訊不足',
            landUse: cadastral?.tName || '資訊不足',
            area: cadastral?.area || 0,
            zoning: zoning?.luse || zoning?.zoneType || zoning?.name || '資訊不足',
            landHeight: undefined, // 필요시 추가
            nearbyInfo: nearbyInfo,
          });
          
          // 응답 데이터 설정
          setAiAnalysisResult(response.data);
          setIsAiDataLoading(false);
        } catch (error) {
          console.error('AI 분석 데이터 로딩 오류:', error);
          console.error('Error details:', error.response?.data || error.message);
          setIsAiDataLoading(false);
        }
      };
      
      fetchAiAnalysis();
    }
  }, [tabCount, props.cadastralData, props.address, props.zoningData, props.transactionData, props.buildingData, aiAnalysisResult, isAiDataLoading]);

  // 건축 가능 규모 계산 (메모이제이션) - early return 전에 호출해야 함
  const buildingScaleData = useMemo(() => {
    const landArea = props.cadastralData?.area;
    const buildRateStr = zoning?.buildRate || zoning?.BUILDRATE;
    const volumeRateStr = zoning?.volumeRate || zoning?.VOLUMERATE;
    const buildingCoverageRatio = buildRateStr ? parseFloat(buildRateStr) : null;
    const floorAreaRatio = volumeRateStr ? parseFloat(volumeRateStr) : null;
    const zoneName = zoning?.luse || zoning?.zoneType || zoning?.name;
    
    const hasLandArea = landArea && landArea > 0;
    const hasBuildRate = buildingCoverageRatio !== null && !isNaN(buildingCoverageRatio);
    const hasVolumeRate = floorAreaRatio !== null && !isNaN(floorAreaRatio);
    
    if (hasLandArea && hasBuildRate && hasVolumeRate) {
      const maxBuildingArea = landArea * (buildingCoverageRatio / 100);
      const maxTotalFloorArea = landArea * (floorAreaRatio / 100);
      const estimatedFloors = Math.round((floorAreaRatio / buildingCoverageRatio) * 10) / 10;
      
      return {
        zoneName,
        buildingCoverageRatio,
        floorAreaRatio,
        maxBuildingArea,
        maxTotalFloorArea,
        estimatedFloors
      };
    }
    
    return null;
  }, [props.cadastralData?.area, zoning?.buildRate, zoning?.volumeRate, zoning?.luse]);

  if (!props.cadastralData) {
    return (
      <div className={`informationWrap ${props.showInformation ? 'show' : ''}`}>
        <div className="loadingModule">
          <svg className="loadingImage" width="184" height={informationHeight === 20 ? "0" : "90"} viewBox="0 0 181 79" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M180,79V43L90,2.4L1.3,43V79H180Z" />
          </svg>
        </div>
      </div>
    );
  }

  const cadastral = props.cadastralData;
  const building = props.buildingData;
  const town = props.townData;
  const village = props.villageData;

  return (
    <div className={`informationWrap ${props.showInformation ? 'show' : ''}`}
      style={windowInnerWidth > 1024 ? {} : { height: `${informationHeight - touchDistanceY}px` }}>
      <div className="M_infoBtn_wrap"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}>
        <div className="M_infoBtn">
          <p className="infoBtn_line"></p>
        </div>
      </div>
      {props.loadingModule === true ?
        <div className={`loadingModule`}>
          <svg className="loadingImage" width="184" height={informationHeight === 20 ? "0" : "90"} viewBox="0 0 181 79" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M180,79V43L90,2.4L1.3,43V79H180Z" />
          </svg>
        </div> : ''}
      <div className={`noneBox ${informationScrollValue > 40 ? 'sticky' : ""}`} ref={noneBoxRef} onScroll={noneBoxScrollEvent}>
        <div className="topWrap">
          <div className="titleBox">
            {(() => {
              const address = props.address;
              if (!address) return cadastral.landNo || "N/A";
              
              let title = '';
              if (address.route) {
                title += address.route;
              }
              if (address.street_number) {
                const number = address.street_number;
                title += (title ? ' ' : '') + (number.includes('號') ? number : number + '號');
              }
              
              return title || cadastral.landNo || "N/A";
            })()}
          </div>
          <div className='subTitleBox'>
            {(() => {
              const address = props.address;
              if (!address) return cadastral.tName || "none";
              
              // 대만 주소 형식: City (市) → District (區)
              // 里(Li/Village)는 공식 주소에서 생략됨
              let subtitle = '';
              if (address.administrative_area_level_1) {
                subtitle += address.administrative_area_level_1;
              }
              if (address.administrative_area_level_2) {
                subtitle += (subtitle ? ' ' : '') + address.administrative_area_level_2;
              }
              
              return subtitle || cadastral.tName || "none";
            })()}
          </div>
          <div className="allowWrap">
            <div className="content">
              <div className="selectWrap">
                <select 
                  className="allowBuildingList"
                  value={selectedBuildingType}
                  onChange={(e) => setSelectedBuildingType(e.target.value)}
                >
                  {applicableBuildingTypes.length > 0 ? (
                    applicableBuildingTypes.map((type) => (
                      <option key={type.code} value={type.code}>
                        {type.name || type.code}
                      </option>
                    ))
                  ) : (
                    <option value="">none</option>
                  )}
                </select>
                <span className="allowBuildingTitle">建築類型</span>
              </div>
            </div>
            {/* <button className="more_btn">更多</button> */}
          </div>
        </div>
        <div className="bottomWrap">
          <div className="bigTabMenuWrap">
            {tabMenu.map((item, index) => {
              return (
                <div 
                  key={index} 
                  className={`bigTabMenu ${tabCount === index + 1 ? "selected" : ""}`}
                  onClick={() => { setTabCount(index + 1) }}
                >
                  {item}
                </div>
              );
            })}
          </div>
          <div className="infoBox">
            <div className="content_container">
              {tabCount === 1 || windowInnerWidth <= 1024 ?
                <div className="tab_01 content_wrap">
                  <div className="content_box">
                    <div className="title">地號</div>
                    <div className="content">{cadastral?.landNo || cadastral?.landCode || "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">面積</div>
                    <div className="content">{cadastral?.area ? cadastral.area.toFixed(2) + " m²" : "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">地區</div>
                    <div className="content">{cadastral?.tName || "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">行政區</div>
                    <div className="content">{town?.name || town?.townName || "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">村里</div>
                    <div className="content">{village?.villName || village?.name || village?.villageName || "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">用途區域</div>
                    <div className="content">{zoning?.luse || zoning?.zoneType || zoning?.name || "none"}</div>
                  </div>
                  <div className="content_box">
                    <div className="title">都市計劃</div>
                    <div className="content">{urbanPlanning?.planName || urbanPlanning?.name || "none"}</div>
                  </div>
                  {buildingScaleData && (
                    <>
                      <div className="content_box" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                        <div className="title" style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>建築可能規模</div>
                      </div>
                      <div className="content_box">
                        <div className="title">用途區域</div>
                        <div className="content">{buildingScaleData.zoneName || "none"}</div>
                      </div>
                      <div className="content_box">
                        <div className="title">建蔽率</div>
                        <div className="content">{buildingScaleData.buildingCoverageRatio}%</div>
                      </div>
                      <div className="content_box">
                        <div className="title">容積率</div>
                        <div className="content">{buildingScaleData.floorAreaRatio}%</div>
                      </div>
                      <div className="content_box">
                        <div className="title">最大建築面積</div>
                        <div className="content">{buildingScaleData.maxBuildingArea.toFixed(2)} m²</div>
                      </div>
                      <div className="content_box">
                        <div className="title">最大延面積</div>
                        <div className="content">{buildingScaleData.maxTotalFloorArea.toFixed(2)} m²</div>
                      </div>
                      <div className="content_box">
                        <div className="title">預估可能層數</div>
                        <div className="content">約 {buildingScaleData.estimatedFloors}層</div>
                      </div>
                    </>
                  )}
                  {selectedBuildingTypeCost && (
                    <>
                      <div className="content_box" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                        <div className="title" style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '10px' }}>預估工程費</div>
                      </div>
                      <div className="content_box">
                        <div className="title">建築類型</div>
                        <div className="content">{selectedBuildingTypeCost.name || selectedBuildingTypeCost.code || "none"}</div>
                      </div>
                      {selectedBuildingTypeCost.costPerSqm !== null && selectedBuildingTypeCost.costPerSqm > 0 ? (
                        <>
                          <div className="content_box">
                            <div className="title">每平方公尺單價</div>
                            <div className="content">{formatPrice(selectedBuildingTypeCost.costPerSqm) + " / m²"}</div>
                          </div>
                          {selectedBuildingTypeCost.estimatedCost !== null && (
                            <div className="content_box">
                              <div className="title" style={{ fontSize: '15px', fontWeight: 'bold' }}>預估總工程費</div>
                              <div className="content" style={{ fontSize: '16px', fontWeight: 'bold', color: '#AB35D5' }}>
                                {formatPrice(selectedBuildingTypeCost.estimatedCost)}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <div className="content_box">
                          <div className="title">預估工程費</div>
                          <div className="content" style={{ color: '#999' }}>資料不足，無法計算</div>
                        </div>
                      )}
                    </>
                  )}
                </div>
                : ""}
              {tabCount === 2 || windowInnerWidth <= 1024 ?
                <div className="tab_02 content_wrap">
                  {props.transactionData && Array.isArray(props.transactionData) && props.transactionData.length > 0 ? (
                    props.transactionData.map((transaction, index) => (
                      <div key={transaction.id || index} style={{ 
                        marginTop: index > 0 ? '30px' : '0', 
                        paddingTop: index > 0 ? '30px' : '0', 
                        borderTop: index > 0 ? '2px solid #e0e0e0' : 'none' 
                      }}>
                        <div className="content_box" style={{ marginBottom: '15px' }}>
                          <div className="title" style={{ fontSize: '16px', fontWeight: 'bold', color: '#AB35D5' }}>
                            交易資訊 {index + 1}
                            {transaction.distance && (
                              <span style={{ fontSize: '12px', fontWeight: 'normal', color: '#999', marginLeft: '10px' }}>
                                (距離: {transaction.distance.toFixed(0)}m)
                              </span>
                            )}
                          </div>
                        </div>
                        {transaction.location && (
                          <div className="content_box">
                            <div className="title">位置</div>
                            <div className="content">{transaction.location}</div>
                          </div>
                        )}
                        {transaction.town && (
                          <div className="content_box">
                            <div className="title">行政區</div>
                            <div className="content">{transaction.town}</div>
                          </div>
                        )}
                        {transaction.caseSubj && (
                          <div className="content_box">
                            <div className="title">案件主題</div>
                            <div className="content">{transaction.caseSubj}</div>
                          </div>
                        )}
                        {transaction.transDate && (
                          <div className="content_box">
                            <div className="title">交易日期</div>
                            <div className="content">
                              {(() => {
                                let dateStr = transaction.transDate.toString();
                                if (dateStr.length === 6) {
                                  return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}`;
                                } else if (dateStr.length === 7) {
                                  return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-0${dateStr.substring(6, 7)}`;
                                } else if (dateStr.length === 8) {
                                  return `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
                                }
                                return dateStr;
                              })()}
                            </div>
                          </div>
                        )}
                        {transaction.lndTrnsfAr && (
                          <div className="content_box">
                            <div className="title">土地轉移面積</div>
                            <div className="content">{transaction.lndTrnsfAr.toFixed(2)} m²</div>
                          </div>
                        )}
                        {transaction.cntInfo && (
                          <div className="content_box">
                            <div className="title">契約資訊</div>
                            <div className="content">{transaction.cntInfo}</div>
                          </div>
                        )}
                        {transaction.buldType && (
                          <div className="content_box">
                            <div className="title">建築類型</div>
                            <div className="content">{transaction.buldType}</div>
                          </div>
                        )}
                        {transaction.buldTotFlr && (
                          <div className="content_box">
                            <div className="title">建築總層數</div>
                            <div className="content">{transaction.buldTotFlr}層</div>
                          </div>
                        )}
                        {transaction.buldMainPu && (
                          <div className="content_box">
                            <div className="title">建築主要用途</div>
                            <div className="content">{transaction.buldMainPu}</div>
                          </div>
                        )}
                        {transaction.buldMainMt && (
                          <div className="content_box">
                            <div className="title">建築主要材料</div>
                            <div className="content">{transaction.buldMainMt}</div>
                          </div>
                        )}
                        {transaction.buldTrnsff && (
                          <div className="content_box">
                            <div className="title">建築轉移</div>
                            <div className="content">{transaction.buldTrnsff}</div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="content_box">
                      <div className="content" style={{ color: '#999', textAlign: 'center', padding: '20px' }}>
                        附近沒有交易資訊
                      </div>
                    </div>
                  )}
                </div>
                : ""}
              {tabCount === 3 || windowInnerWidth <= 1024 ?
                <div className="tab_03 content_wrap">
                  
                  {isAiDataLoading ? (
                    <div className="content_box" style={{ textAlign: 'center', padding: '40px 20px' }}>
                      <div className="content" style={{ color: '#999' }}>AI 分析中，請稍候...</div>
                    </div>
                  ) : aiAnalysisResult ? (
                    <div className="content_wrap">
                      <div className="ai_land_info_wrap">
                        <div className="top_wrap_border">
                          <div className="top_wrap">
                            <RadarChartComponent data={[
                              aiAnalysisResult.토지가격,
                              aiAnalysisResult.투자가치,
                              aiAnalysisResult['법적 규제'] || 0,
                              aiAnalysisResult.소음 || 0,
                              aiAnalysisResult.인구규모 || 0,
                              aiAnalysisResult.교통및접근성 || 0,
                              aiAnalysisResult['지형 및 토양'] || 0,
                              aiAnalysisResult.편의시설 || 0,
                              aiAnalysisResult['인프라 및 공공시설'] || 0,
                            ]} />
                            <div className="row white"></div>
                            <div className="bottom_wrap">
                              <div className="left_wrap">
                                <div className="title">SCORE</div>
                                <div className="point">
                                  <img src={`/images/map/ai_land_score_${getGradeFromScore(parseFloat(aiAnalysisResult.SCORE)).toLowerCase()}.png`} alt={getGradeFromScore(parseFloat(aiAnalysisResult.SCORE))} />
                                  <div className="score_text">{aiAnalysisResult.SCORE}</div>
                                </div>
                              </div>
                              <div className="right_wrap">
                                <div className="titles">
                                  <div className="title">AI 評估價值</div>
                                </div>
                                <div className="row verticle"></div>
                                <div className="prices">
                                  {aiAnalysisResult.AI評估價值 && (
                                    <div className="price">
                                      {(() => {
                                        const value = aiAnalysisResult.AI評估價值;
                                        // 숫자 문자열인 경우 포맷팅
                                        if (typeof value === 'string' && /^\d+$/.test(value)) {
                                          return parseInt(value, 10).toLocaleString() + '(元/㎡)';
                                        }
                                        // 이미 포맷된 문자열인 경우 그대로 표시
                                        return value + (value.includes('元') || value.includes('㎡') ? '' : ' (元/㎡)');
                                      })()}
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {(aiAnalysisResult.AI分析摘要 || aiAnalysisResult.AI분석요약) && (
                          <div className="ai_summary">
                            <div className="title">
                              <img src="/images/map/ai_summary_icon.png" alt="AI Summary" />
                              AI 分析摘要
                            </div>
                            <div className="des">
                              {aiAnalysisResult.AI分析摘要 || aiAnalysisResult.AI분석요약}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="content_box" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                      <div className="title" style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '15px' }}>土地資訊</div>
                      <div className="content_box">
                        <div className="title">地號</div>
                        <div className="content">{cadastral?.landNo || cadastral?.landCode || "none"}</div>
                      </div>
                      <div className="content_box">
                        <div className="title">面積</div>
                        <div className="content">{cadastral?.area ? cadastral.area.toFixed(2) + " m²" : "none"}</div>
                      </div>
                      <div className="content_box">
                        <div className="title">用途區域</div>
                        <div className="content">{zoning?.luse || zoning?.zoneType || zoning?.name || "none"}</div>
                      </div>
                      {props.address?.route && (
                        <div className="content_box">
                          <div className="title">地址</div>
                          <div className="content">
                            {props.address.administrative_area_level_1} {props.address.administrative_area_level_2} {props.address.route}
                            {props.address.street_number && ` ${props.address.street_number.includes('號') ? props.address.street_number : props.address.street_number + '號'}`}
                          </div>
                        </div>
                      )}
                      <div className="content_box" style={{ marginTop: '20px', textAlign: 'center', padding: '20px' }}>
                        <div className="content" style={{ color: '#999' }}>
                          點擊上方按鈕開始 AI 分析
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                : ""}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Information;

